// The app talks to this store, never to a backend directly. Today it is a
// local reactive store seeded with demo data and persisted to localStorage;
// swapping in the Supabase adapter means reimplementing this surface, not
// touching a single component.

import { seed } from './demo.js';
import { backendMode } from './backend/mode.js';
import { buildSchedule, dateKeyInZone, addDays } from '../lib/recurrence.js';
import {
  bookingToDomain, occurrenceKey, membershipMap, memberMap, planMap,
  invoiceMap, policyMap, freezeMap, toDomain, toRow,
} from './backend/mapping.js';
import {
  requestBooking, cancelBooking, reindexWaitlist, settleAttendance,
  BOOKING_STATUS, OCCUPIES_SEAT,
} from '../lib/booking.js';
import { checkEntitlement, selectMembership, PLAN_KINDS } from '../lib/entitlements.js';
import { mrrCents, nextDunningStep } from '../lib/billing.js';
import { format as formatMoney } from '../lib/money.js';

const KEY = 'floatdesk.state.v1';

// In supabase mode the snapshot arrives from the server after sign-in. Until
// then the shape has to be complete and empty rather than absent, because every
// selector indexes into it during the first render.
const EMPTY = {
  org: { id: null, name: 'Loading…', timeZone: 'UTC', currency: 'USD', taxRateBps: 0 },
  locations: [], staff: [], plans: [], members: [], memberships: [],
  templates: [], rooms: [], bookings: [], invoices: [],
  policy: structuredClone(seed.policy),
};

function load() {
  if (backendMode() === 'supabase') return structuredClone(EMPTY);
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // Private windows and blocked storage are normal; fall through to seed.
  }
  return structuredClone(seed);
}

function persist(next) {
  // Server-backed data is not cached to localStorage: it would go stale behind
  // the real database and quietly serve another org's rows after a switch.
  if (backendMode() === 'supabase') return;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Non-fatal: the app works fine without persistence.
  }
}

let state = load();
const listeners = new Set();

function notify() {
  // A NEW top-level object every time, deliberately. Actions mutate fields on
  // `state` (state.bookings = [...]), which leaves the object identity intact —
  // and useSyncExternalStore bails out of re-rendering when Object.is(prev,next)
  // is true. Without this line the store updates, localStorage updates, and the
  // screen does not move until something else forces a remount.
  state = { ...state, version: (state.version || 0) + 1 };
  persist(state);
  listeners.forEach((fn) => fn(state));
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getState() {
  return state;
}

/** Replace the whole snapshot with one loaded from the backend. */
export function hydrate(snapshot) {
  state = { ...snapshot };
  notify();
}

export function resetToSeed() {
  state = structuredClone(seed);
  notify();
}

/**
 * Ids are uuids, not prefixed random strings.
 *
 * Every primary key in the schema is a uuid, and the client generates the id
 * so the optimistic row and the server row are the same row — no id mapping on
 * the way back, and React keys that do not change under the user. The old
 * `bk_a1b2c3d` shape could not be inserted at all.
 */
const nextId = () => (globalThis.crypto?.randomUUID
  ? globalThis.crypto.randomUUID()
  // Node 18 and older browsers. Not cryptographically interesting — the id
  // only has to be unique.
  : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  }));

/* ------------------------------------------------------------- write seam */

// Installed by the auth gate once there is a session. Absent in demo mode,
// which is what keeps the demo working with no network at all.
let writer = null;

export function setWriter(fn) {
  writer = fn;
}

export const isPersisting = () => Boolean(writer);

// The server occasionally disagrees with an optimistic guess — the class
// filled between the click and the request arriving. That is not an error to
// swallow: the operator was told one thing and something else is true.
const correctionListeners = new Set();

export function subscribeCorrections(fn) {
  correctionListeners.add(fn);
  return () => correctionListeners.delete(fn);
}

function announce(correction) {
  correctionListeners.forEach((fn) => fn(correction));
}

/**
 * Replace this occurrence's rows with the server's, wholesale.
 *
 * Wholesale, not merged: a booking the server did not send is a booking that
 * no longer exists, and that is exactly how an optimistic row that was refused
 * disappears. Merging would leave it on screen forever.
 */
function applyServerRows(rows) {
  if (!rows) return;
  const occurrenceId = occurrenceKey(rows.templateId, rows.onDate);
  const fresh = (rows.bookings || []).map(bookingToDomain);

  state.bookings = [
    ...state.bookings.filter((b) => b.occurrenceId !== occurrenceId),
    ...fresh,
  ];

  // Memberships come back with their scalar fields only. Freezes and usage
  // dates are assembled at load time from other tables, so they are preserved
  // rather than blanked — except this occurrence's own usage, which is
  // recomputed from the rows that just arrived.
  const byId = new Map((rows.memberships || []).map((m) => [m.id, m]));
  if (byId.size > 0) {
    state.memberships = state.memberships.map((m) => {
      const row = byId.get(m.id);
      if (!row) return m;
      const usedHere = fresh.some((b) => b.membershipId === m.id && b.consumedUsage);
      const usageDates = (m.usageDates || []).filter((d) => d !== rows.onDate);
      return {
        ...m,
        ...toDomain(membershipMap, row),
        freezes: m.freezes || [],
        usageDates: usedHere ? [...usageDates, rows.onDate].sort() : usageDates,
      };
    });
  }

  notify();
}

/**
 * Send a mutation, then reconcile against whatever the server says is true.
 *
 * Fire-and-reconcile rather than await: every call site reads the result
 * synchronously (`const r = book(...); if (!r.ok) …`), so the optimistic
 * decision is what the operator sees immediately and the server's answer
 * arrives as a correction if it differs.
 */
function submit(kind, args, { occurrenceId, expected } = {}) {
  if (!writer) return;

  Promise.resolve(writer[kind](args))
    .then((envelope) => {
      if (!envelope) return;
      applyServerRows(envelope.rows);

      if (envelope.transportFailed) {
        announce({
          tone: 'bad',
          message: `Not saved — ${envelope.reason} It will not be there when you reload.`,
        });
        return;
      }
      if (envelope.ok === false) {
        announce({
          tone: 'bad',
          message: refusalMessage(envelope.code, occurrenceId),
        });
        return;
      }
      // Booked optimistically, waitlisted in fact — someone else was quicker.
      if (expected && envelope.action && envelope.action !== expected) {
        announce({
          tone: 'warn',
          message: envelope.action === 'waitlist'
            ? 'That class filled up first — added to the waitlist instead.'
            : `Saved as ${envelope.action}.`,
        });
      }
    })
    .catch((err) => {
      announce({ tone: 'bad', message: `Not saved — ${err.message}` });
    });
}

/**
 * Persist an uncontended change straight to its table.
 *
 * Members, plans, holds, invoices and class overrides are field-level writes
 * on rows nobody is racing for. RLS already scopes them to the org and the
 * role, so wrapping them in RPCs would add surface without adding safety.
 *
 * Like submit(), this does not block the caller. Unlike submit(), there is no
 * server decision to reconcile against — only whether it saved, so a failure
 * says so plainly rather than trying to undo a change the operator can see.
 */
function persistRow(op, table, arg, row, label) {
  if (!writer) return;
  // `override` upserts a whole row and needs no table name; the rest take
  // (table, id, patch).
  const call = op === 'override' ? writer.override(arg) : writer[op](table, arg, row);
  Promise.resolve(call)
    .then((r) => {
      if (r.ok) return;
      announce({
        tone: 'bad',
        message: `${label} was not saved — ${r.reason}. It will not be there when you reload.`,
      });
    })
    .catch((err) => announce({ tone: 'bad', message: `${label} was not saved — ${err.message}` }));
}

const REFUSAL_TEXT = {
  already_booked: 'They were already booked into that class.',
  full_no_waitlist: 'That class filled up before this saved.',
  booking_closed: 'Booking had already closed for that class.',
  not_open_yet: 'Booking has not opened for that class yet.',
  class_cancelled: 'That class has been cancelled.',
  class_not_found: 'That class no longer exists.',
  no_credits: 'They had no credits left by the time this saved.',
  credits_expired: 'Their credits had expired.',
  frozen: 'Their membership is on hold.',
  cancelled: 'Their membership was cancelled.',
  expired: 'Their membership had expired.',
  not_started: 'Their membership had not started.',
  no_membership: 'They have no membership on file.',
  already_resolved: 'That booking had already been settled.',
  not_allowed: 'You do not have permission to do that.',
  not_found: 'That booking no longer exists.',
  no_policy: 'This gym has no booking policy configured.',
};

function refusalMessage(code) {
  return `Not saved — ${REFUSAL_TEXT[code] || `the database refused it (${code}).`}`;
}

/* ---------------------------------------------------------------- selectors */

// The gym's date, not the server's. See dateKeyInZone.
export const today = () => dateKeyInZone(new Date(), state.org?.timeZone || 'UTC');

export function planById(id) {
  return state.plans.find((p) => p.id === id) || null;
}

export function memberById(id) {
  return state.members.find((m) => m.id === id) || null;
}

export function staffById(id) {
  return state.staff.find((s) => s.id === id) || null;
}

export function roomById(id) {
  return (state.rooms || []).find((r) => r.id === id) || null;
}

export function membershipsFor(memberId) {
  return state.memberships.filter((m) => m.memberId === memberId);
}

export function activeMembershipFor(memberId) {
  return membershipsFor(memberId).find((m) => m.status === 'active') || null;
}

/**
 * Occurrences in a date range, with seat counts.
 *
 * `includeCancelled` adds the dates the gym called off, flagged rather than
 * bookable. They are excluded by default because nobody can book them — but a
 * class that simply vanishes from the schedule leaves staff wondering whether
 * they imagined it, and gives them no way to undo the cancellation.
 */
export function scheduleBetween(fromKey, toKey, { includeCancelled = false } = {}) {
  const withCounts = (occ) => {
    const template = state.templates.find((t) => t.id === occ.templateId);
    const seats = bookingsFor(occ.id);
    return {
      ...occ,
      program: template?.program,
      // Same rule as booking.seatCounts: a checked-in member is still in the
      // room. Counting only BOOKED here would let the schedule disagree with
      // the engine about whether a class is full.
      booked: seats.filter((b) => OCCUPIES_SEAT.includes(b.status)).length,
      waitlisted: seats.filter((b) => b.status === BOOKING_STATUS.WAITLISTED).length,
    };
  };

  const live = buildSchedule(state.templates, fromKey, toKey).map(withCounts);
  if (!includeCancelled) return live;

  // Re-expand each template with its exceptions removed, then keep only the
  // dates the real expansion dropped.
  const cancelled = state.templates.flatMap((t) => {
    const dates = (t.exceptions || []).filter((d) => d >= fromKey && d <= toKey);
    if (dates.length === 0) return [];
    return buildSchedule([{ ...t, exceptions: [] }], fromKey, toKey)
      .filter((o) => dates.includes(o.date))
      .map((o) => ({ ...withCounts(o), cancelled: true }));
  });

  return [...live, ...cancelled].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}

export function bookingsFor(occurrenceId) {
  return state.bookings.filter((b) => b.occurrenceId === occurrenceId);
}

export function bookingsForMember(memberId) {
  return state.bookings.filter((b) => b.memberId === memberId);
}

export function occurrenceById(occurrenceId) {
  const [templateId, date] = occurrenceId.split(':');
  return scheduleBetween(date, date).find((o) => o.id === occurrenceId)
    || buildSchedule(state.templates.filter((t) => t.id === templateId), date, date)[0]
    || null;
}

/** Everything the front desk needs to decide whether to let someone in. */
export function entitlementFor(memberId, occurrence, dateKey = today()) {
  const held = membershipsFor(memberId);
  const plansById = Object.fromEntries(state.plans.map((p) => [p.id, p]));
  const chosen = selectMembership(held, plansById, { occurrence, dateKey });
  if (chosen) {
    return { membership: chosen, plan: planById(chosen.planId), result: { ok: true } };
  }
  // Nothing worked — surface the most informative refusal rather than a blank no.
  const first = held[0];
  const result = checkEntitlement({
    membership: first || null,
    plan: first ? planById(first.planId) : null,
    occurrence,
    dateKey,
  });
  return { membership: first || null, plan: first ? planById(first.planId) : null, result };
}

export function dashboardStats() {
  const active = state.members.filter((m) => m.status === 'active').length;
  const withAmounts = state.memberships.map((m) => ({
    ...m, amountCents: m.amountCents ?? planById(m.planId)?.amountCents ?? 0,
  }));
  const pastDue = state.invoices.filter((i) => i.status === 'past_due');
  const t = today();
  const todaysClasses = scheduleBetween(t, t);
  const capacity = todaysClasses.reduce((a, o) => a + o.capacity, 0);
  const filled = todaysClasses.reduce((a, o) => a + o.booked, 0);
  const frozen = state.memberships.filter(
    (m) => (m.freezes || []).some((f) => t >= f.from && (!f.to || t <= f.to)),
  ).length;

  const todayIds = new Set(todaysClasses.map((o) => o.id));
  const checkedInToday = state.bookings.filter(
    (b) => b.status === BOOKING_STATUS.ATTENDED && todayIds.has(b.occurrenceId),
  ).length;

  return {
    activeMembers: active,
    mrrCents: mrrCents(withAmounts),
    pastDueCount: pastDue.length,
    pastDueCents: pastDue.reduce((a, i) => a + i.totalCents, 0),
    classesToday: todaysClasses.length,
    fillRate: capacity ? Math.round((filled / capacity) * 100) : 0,
    frozenCount: frozen,
    seatsToday: { filled, capacity },
    checkedInToday,
    expectedToday: filled + checkedInToday,
  };
}

export function dunningQueue() {
  const t = today();
  return state.invoices
    .filter((i) => i.status === 'past_due')
    .map((i) => ({
      invoice: i,
      member: memberById(i.memberId),
      step: nextDunningStep({
        failedOnKey: i.failedOnKey, attemptsMade: i.attemptsMade || 0, todayKey: t,
      }),
    }))
    .sort((a, b) => (a.step.action === 'retry' ? -1 : 1) - (b.step.action === 'retry' ? -1 : 1));
}

/** Booked seats per day for the next N days, for the dashboard strip. */
export function weekAhead(days = 7) {
  const from = today();
  const to = addDays(from, days - 1);
  const occurrences = scheduleBetween(from, to);
  return Array.from({ length: days }, (_, i) => {
    const date = addDays(from, i);
    const onDay = occurrences.filter((o) => o.date === date);
    return {
      date,
      classes: onDay.length,
      booked: onDay.reduce((a, o) => a + o.booked, 0),
      capacity: onDay.reduce((a, o) => a + o.capacity, 0),
    };
  });
}

export const ATTENTION = {
  PAST_DUE: 'past_due',
  NO_WAIVER: 'no_waiver',
  CREDITS_LOW: 'credits_low',
  CREDITS_EXPIRING: 'credits_expiring',
  MEMBERSHIP_ENDING: 'membership_ending',
  HOLD_ENDING: 'hold_ending',
};

/**
 * Everything a manager should look at today, not just failed payments.
 *
 * The dashboard used to show the dunning queue alone, which is the one thing
 * that already screams for itself on the Billing screen. The quiet ones —
 * a pack about to expire unused, a member training without a signed waiver,
 * a hold about to lapse into an unexpected charge — are the ones that cost
 * money precisely because nothing surfaces them.
 */
export function attentionQueue({ horizonDays = 14, lowCredits = 2 } = {}) {
  const t = today();
  const soon = addDays(t, horizonDays);
  const items = [];

  for (const { invoice, member, step } of dunningQueue()) {
    items.push({
      kind: ATTENTION.PAST_DUE,
      memberId: invoice.memberId,
      member,
      detail: `${formatMoney(invoice.totalCents, invoice.currency || state.org.currency)} outstanding`,
      action: step.action,
      urgent: step.action === 'retry' || step.action === 'needs_review',
    });
  }

  for (const m of state.members) {
    if (m.status === 'lead' || m.status === 'archived') continue;
    if (m.waiverSignedAt) continue;
    // Only worth flagging for someone who is actually training.
    if (!activeMembershipFor(m.id)) continue;
    items.push({
      kind: ATTENTION.NO_WAIVER,
      memberId: m.id,
      member: m,
      detail: 'Training without a signed waiver',
      urgent: true,
    });
  }

  for (const s of state.memberships) {
    if (s.status !== 'active') continue;
    const member = memberById(s.memberId);
    const plan = planById(s.planId);
    const isCredit = plan && [PLAN_KINDS.PACK, PLAN_KINDS.DROP_IN].includes(plan.kind);

    if (isCredit && s.creditsRemaining > 0 && s.creditsRemaining <= lowCredits) {
      items.push({
        kind: ATTENTION.CREDITS_LOW,
        memberId: s.memberId,
        member,
        detail: `${s.creditsRemaining} of ${plan.name} left`,
        urgent: false,
      });
    }

    // Credits already paid for that are about to be lost is the case an owner
    // most wants to catch: it is a renewal conversation, not a refund one.
    if (isCredit && s.creditsRemaining > 0 && s.creditsExpireOn
      && s.creditsExpireOn >= t && s.creditsExpireOn <= soon) {
      items.push({
        kind: ATTENTION.CREDITS_EXPIRING,
        memberId: s.memberId,
        member,
        detail: `${s.creditsRemaining} credits expire ${s.creditsExpireOn}`,
        urgent: true,
      });
    }

    if (s.cancelScheduled && s.endDate && s.endDate >= t && s.endDate <= soon) {
      items.push({
        kind: ATTENTION.MEMBERSHIP_ENDING,
        memberId: s.memberId,
        member,
        detail: `Membership ends ${s.endDate}`,
        urgent: false,
      });
    }

    for (const f of s.freezes || []) {
      if (!f.to || f.to < t || f.to > soon) continue;
      items.push({
        kind: ATTENTION.HOLD_ENDING,
        memberId: s.memberId,
        member,
        detail: `Hold ends ${f.to}, billing resumes`,
        urgent: false,
      });
    }
  }

  // Urgent first, then alphabetically, so the list is stable between renders
  // rather than reshuffling every time something changes.
  return items.sort((a, b) => (b.urgent === true) - (a.urgent === true)
    || (a.member?.name ?? '').localeCompare(b.member?.name ?? ''));
}

/* ------------------------------------------------------------------ actions */

export function book(memberId, occurrenceId) {
  const occurrence = occurrenceById(occurrenceId);
  if (!occurrence) return { ok: false, reason: 'Class not found.' };

  const { membership, plan } = entitlementFor(memberId, occurrence, occurrence.date);
  const decision = requestBooking({
    occurrence,
    bookings: bookingsFor(occurrenceId),
    membership,
    plan,
    memberId,
    now: new Date().toISOString(),
    policy: state.policy,
  });
  if (!decision.ok) return decision;

  // A waitlist seat consumes nothing; that happens on promotion.
  const takesSeat = decision.action === 'book';
  const consumesCredit = takesSeat && decision.entitlement.consumes === 'credit';
  // Mirrors consumedCredit for capped plans. The server derives a membership's
  // usageDates from this column, so a booking that omits it is usage the next
  // reload cannot see, and the member silently gets their cap back.
  const consumesUsage = takesSeat && decision.entitlement.consumes === 'usage';

  const bookingId = nextId();
  state.bookings = [...state.bookings, {
    id: bookingId,
    orgId: state.org.id,
    occurrenceId,
    memberId,
    membershipId: membership?.id ?? null,
    status: takesSeat ? BOOKING_STATUS.BOOKED : BOOKING_STATUS.WAITLISTED,
    waitlistPosition: decision.position ?? null,
    consumedCredit: consumesCredit,
    consumedUsage: consumesUsage,
    bookedAt: new Date().toISOString(),
  }];

  if (membership) applyConsumption(membership.id, decision.entitlement, occurrence.date, decision.action);
  notify();

  submit('book_seat', {
    mutationId: nextId(),
    bookingId,
    memberId,
    occurrenceId,
    membershipId: membership?.id ?? null,
  }, { occurrenceId, expected: decision.action });

  return decision;
}

function applyConsumption(membershipId, entitlement, dateKey, action) {
  if (action !== 'book') return; // waitlist seats consume nothing
  state.memberships = state.memberships.map((m) => {
    if (m.id !== membershipId) return m;
    if (entitlement.consumes === 'credit') {
      return { ...m, creditsRemaining: Math.max(0, (m.creditsRemaining || 0) - 1) };
    }
    if (entitlement.consumes === 'usage') {
      return { ...m, usageDates: [...(m.usageDates || []), dateKey] };
    }
    return m;
  });
}

export function cancel(bookingId) {
  const booking = state.bookings.find((b) => b.id === bookingId);
  if (!booking) return { ok: false, code: 'not_found', reason: 'Booking not found.' };

  // book() guards this and cancel() did not. A booking can outlive its class:
  // delete the template, or shorten its date range, and occurrenceById returns
  // null -- then cancelBooking dereferences occurrence.startsAt and the screen
  // dies on a member simply trying to cancel.
  const occurrence = occurrenceById(booking.occurrenceId);
  if (!occurrence) {
    state.bookings = state.bookings.map((b) => (b.id === bookingId
      ? { ...b, status: BOOKING_STATUS.CANCELLED, cancelledAt: new Date().toISOString() }
      : b));
    state.bookings = reindexWaitlist(state.bookings);
    notify();
    return {
      ok: true,
      bookingId,
      status: BOOKING_STATUS.CANCELLED,
      feeCents: 0,
      returnsCredit: false,
      promoted: null,
      note: 'The class no longer exists, so no cancellation fee was applied.',
    };
  }

  const effects = cancelBooking({
    occurrence,
    bookings: bookingsFor(booking.occurrenceId),
    bookingId,
    now: new Date().toISOString(),
    policy: state.policy,
  });
  if (!effects.ok) return effects;

  // A waitlisted seat cost nothing when it was taken, so entitlement has to be
  // re-checked at the moment it becomes a real one. Between joining the list
  // and being promoted a member can spend their last credit elsewhere, hit
  // their weekly cap, have their pack expire, or be frozen — and promoting
  // them anyway hands out a free seat that the balance cannot pay for.
  if (effects.promoted) {
    const candidate = state.bookings.find((b) => b.id === effects.promoted.bookingId);
    const check = candidate
      ? entitlementFor(candidate.memberId, occurrence, occurrence.date)
      : null;
    if (!check?.result.ok) {
      effects.promotionRefused = {
        memberId: candidate?.memberId ?? null,
        reason: check?.result.reason ?? 'Booking not found.',
      };
      effects.promoted = null;
    }
  }

  state.bookings = state.bookings.map((b) => {
    if (b.id === bookingId) {
      return { ...b, status: effects.status, feeCents: effects.feeCents, cancelledAt: new Date().toISOString() };
    }
    if (effects.promoted && b.id === effects.promoted.bookingId) {
      return { ...b, status: BOOKING_STATUS.BOOKED, waitlistPosition: null, promotedAt: new Date().toISOString() };
    }
    return b;
  });
  state.bookings = reindexWaitlist(state.bookings);

  if (effects.returnsCredit && booking.membershipId) {
    state.memberships = state.memberships.map((m) => (m.id === booking.membershipId
      ? { ...m, creditsRemaining: (m.creditsRemaining || 0) + 1 }
      : m));
  }

  // A promotion takes a real seat, so it consumes what a seat costs. Skipping
  // this let every promoted member train free off a pack, and let a capped
  // plan exceed its cap by one for each promotion.
  if (effects.promoted?.consumesCredit) {
    const promoted = state.bookings.find((b) => b.id === effects.promoted.bookingId);
    const membership = promoted?.membershipId
      ? state.memberships.find((m) => m.id === promoted.membershipId)
      : null;
    const plan = membership ? planById(membership.planId) : null;
    const debitsCredit = plan && [PLAN_KINDS.PACK, PLAN_KINDS.DROP_IN].includes(plan.kind);
    const debitsUsage = plan?.kind === PLAN_KINDS.RECURRING_CAPPED;

    if (debitsCredit || debitsUsage) {
      state.memberships = state.memberships.map((m) => {
        if (m.id !== membership.id) return m;
        return debitsCredit
          ? { ...m, creditsRemaining: Math.max(0, (m.creditsRemaining || 0) - 1) }
          : { ...m, usageDates: [...(m.usageDates || []), occurrence.date] };
      });

      // consumedCredit means "this seat cost a credit", and only a credit plan
      // can make that true. Stamping it for every promotion gave unlimited and
      // capped members a refund flag on a balance they do not have: promote an
      // unlimited member off a waitlist, cancel, and their credit count goes
      // from 0 to 1 — a credit minted from nothing.
      state.bookings = state.bookings.map((b) => (b.id === promoted.id
        ? { ...b, consumedCredit: debitsCredit, consumedUsage: debitsUsage }
        : b));
    }
  }

  notify();
  submit('cancel_seat', { mutationId: nextId(), bookingId }, { occurrenceId: booking.occurrenceId });
  return effects;
}

export function checkIn(bookingId) {
  state.bookings = state.bookings.map((b) => (b.id === bookingId
    ? { ...b, checkedInAt: new Date().toISOString(), status: BOOKING_STATUS.ATTENDED }
    : b));
  notify();
  submit('set_attendance', { mutationId: nextId(), bookingId, present: true });
}

export function undoCheckIn(bookingId) {
  state.bookings = state.bookings.map((b) => (b.id === bookingId
    ? { ...b, checkedInAt: null, status: BOOKING_STATUS.BOOKED }
    : b));
  notify();
  submit('set_attendance', { mutationId: nextId(), bookingId, present: false });
}

/**
 * Patch a single date of a recurring class.
 *
 * Occurrences are computed, not stored, so a one-day change is an override on
 * the template rather than an edit to a row. `patch` accepts staffId, roomId,
 * capacity, time, durationMin and name; passing null for a key clears it and
 * the class falls back to the template.
 */
export function overrideOccurrence(occurrenceId, patch) {
  const [templateId, date] = [
    occurrenceId.slice(0, occurrenceId.lastIndexOf(':')),
    occurrenceId.slice(occurrenceId.lastIndexOf(':') + 1),
  ];
  const template = state.templates.find((t) => t.id === templateId);
  if (!template) return { ok: false, reason: 'Class not found.' };

  // Capacity cannot drop below the people already holding seats — the honest
  // alternative is to tell someone they are no longer booked, and that is a
  // conversation, not a side effect of a form.
  if (patch.capacity != null) {
    const taken = bookingsFor(occurrenceId)
      .filter((b) => [BOOKING_STATUS.BOOKED, BOOKING_STATUS.ATTENDED].includes(b.status)).length;
    if (patch.capacity < taken) {
      return {
        ok: false,
        reason: `${taken} people are already booked. Cancel someone before dropping capacity to ${patch.capacity}.`,
      };
    }
  }

  const merged = { ...(template.overrides?.[date] || {}) };
  for (const [k, v] of Object.entries(patch)) {
    if (v === null || v === undefined || v === '') delete merged[k];
    else merged[k] = v;
  }

  state.templates = state.templates.map((t) => (t.id === templateId
    ? {
      ...t,
      overrides: Object.keys(merged).length
        ? { ...(t.overrides || {}), [date]: merged }
        : Object.fromEntries(Object.entries(t.overrides || {}).filter(([d]) => d !== date)),
    }
    : t));
  notify();
  persistRow('override', null, {
    org_id: state.org.id,
    template_id: templateId,
    on_date: date,
    staff_id: merged.staffId ?? null,
    room_id: merged.roomId ?? null,
    capacity: merged.capacity ?? null,
    name: merged.name ?? null,
    start_time: merged.time ?? null,
    duration_min: merged.durationMin ?? null,
    cancelled: false,
  }, undefined, 'The class change');
  return { ok: true, date, patch: merged };
}

/**
 * Cancel one date of a recurring class.
 *
 * Everyone holding a seat is released without a fee and with their credit
 * back, whatever the late-cancel policy says: the gym cancelled, not them, and
 * charging a member for the gym's decision is how a support ticket starts.
 */
export function cancelOccurrence(occurrenceId, { reason } = {}) {
  const [templateId, date] = [
    occurrenceId.slice(0, occurrenceId.lastIndexOf(':')),
    occurrenceId.slice(occurrenceId.lastIndexOf(':') + 1),
  ];
  const template = state.templates.find((t) => t.id === templateId);
  if (!template) return { ok: false, reason: 'Class not found.' };

  const affected = bookingsFor(occurrenceId).filter(
    (b) => [BOOKING_STATUS.BOOKED, BOOKING_STATUS.WAITLISTED].includes(b.status),
  );

  const refunds = new Map();
  for (const b of affected) {
    if (b.consumedCredit && b.membershipId) {
      refunds.set(b.membershipId, (refunds.get(b.membershipId) || 0) + 1);
    }
  }

  const ids = new Set(affected.map((b) => b.id));
  state.bookings = state.bookings.map((b) => (ids.has(b.id)
    ? {
      ...b,
      status: BOOKING_STATUS.CANCELLED,
      feeCents: 0,
      cancelledAt: new Date().toISOString(),
      cancelledByGym: true,
    }
    : b));

  state.memberships = state.memberships.map((m) => {
    const back = refunds.get(m.id);
    if (!back) return m;
    return { ...m, creditsRemaining: (m.creditsRemaining || 0) + back };
  });

  state.templates = state.templates.map((t) => (t.id === templateId
    ? { ...t, exceptions: [...new Set([...(t.exceptions || []), date])] }
    : t));

  notify();
  persistRow('override', null, {
    org_id: state.org.id,
    template_id: templateId,
    on_date: date,
    cancelled: true,
  }, undefined, 'The cancellation');
  // Everyone released, one call each: cancel_seat is the only path that may
  // write a booking, and it returns the credit and charges no fee.
  for (const b of affected) {
    submit('cancel_seat', { mutationId: nextId(), bookingId: b.id }, { occurrenceId });
  }
  return {
    ok: true,
    date,
    released: affected.length,
    creditsReturned: [...refunds.values()].reduce((a, b) => a + b, 0),
    reason: reason || null,
  };
}

/** Put a cancelled date back on the schedule. Bookings are not restored. */
export function restoreOccurrence(occurrenceId) {
  const [templateId, date] = [
    occurrenceId.slice(0, occurrenceId.lastIndexOf(':')),
    occurrenceId.slice(occurrenceId.lastIndexOf(':') + 1),
  ];
  state.templates = state.templates.map((t) => (t.id === templateId
    ? { ...t, exceptions: (t.exceptions || []).filter((d) => d !== date) }
    : t));
  notify();
  persistRow('override', null, {
    org_id: state.org.id, template_id: templateId, on_date: date, cancelled: false,
  }, undefined, 'Restoring the class');
  return { ok: true, date };
}

/** Is this date cancelled? occurrenceById cannot say — it stops expanding it. */
export function isOccurrenceCancelled(occurrenceId) {
  const at = occurrenceId.lastIndexOf(':');
  const templateId = occurrenceId.slice(0, at);
  const date = occurrenceId.slice(at + 1);
  const template = state.templates.find((t) => t.id === templateId);
  return Boolean(template && (template.exceptions || []).includes(date));
}

/** How long after a class ends before it is treated as finished. */
export const SETTLE_GRACE_MIN = 60;

/** Bookings on this class that a human still has to resolve. */
export function unresolvedFor(occurrenceId) {
  return bookingsFor(occurrenceId).filter((b) => b.status === BOOKING_STATUS.BOOKED);
}

/** Has this class ended long enough ago to be worth closing out? */
export function isSettleable(occurrence, now = Date.now()) {
  if (!occurrence?.endsAt) return false;
  return now > new Date(occurrence.endsAt).getTime() + SETTLE_GRACE_MIN * 60000;
}

/**
 * Close out a finished class: whoever was checked in attended, everyone else
 * is a no-show and owes the policy's fee.
 *
 * Deliberately a staff action rather than a timer or a background sweep.
 * settleAttendance has existed and been tested since Phase 0 and nothing ever
 * called it, so no-show fees had never once been charged. Making it automatic
 * would have fixed that by silently billing members for a rule the gym had
 * never actually enforced — the first anyone would hear of it is a chargeback.
 * A human presses the button, having seen the fee named on it.
 */
export function settleClass(occurrenceId) {
  const seats = bookingsFor(occurrenceId);
  if (seats.length === 0) return { ok: false, reason: 'Nothing booked into this class.' };

  // Only rows that were still awaiting a decision. settleAttendance passes
  // everything else through untouched, so counting its whole output would
  // report the same no-shows again on a second press and read as a second
  // round of fees — which is precisely the thing a fee-charging button must
  // never be ambiguous about.
  const pending = seats.filter((b) => b.status === BOOKING_STATUS.BOOKED);
  if (pending.length === 0) {
    return { ok: true, attended: 0, noShows: 0, feeCents: 0, alreadySettled: true };
  }

  const settled = settleAttendance({ bookings: pending, policy: state.policy });
  const byId = new Map(settled.map((b) => [b.id, b]));
  state.bookings = state.bookings.map((b) => byId.get(b.id) ?? b);

  // noShows is what THIS call changed, because that is what the fee is charged
  // on. attended is the class total, because that is what a human reading the
  // confirmation wants to know — check-in already resolved those rows, so
  // counting only what settling promoted would always report zero.
  const noShows = settled.filter((b) => b.status === BOOKING_STATUS.NO_SHOW).length;
  const attended = bookingsFor(occurrenceId)
    .filter((b) => b.status === BOOKING_STATUS.ATTENDED).length;

  notify();
  submit('settle_class', { mutationId: nextId(), occurrenceId }, { occurrenceId });
  return {
    ok: true,
    attended,
    noShows,
    feeCents: noShows * (state.policy.noShowFeeCents || 0),
  };
}

export function addMember(fields) {
  const member = {
    id: nextId(),
    orgId: state.org.id,
    locationId: state.locations[0].id,
    status: 'active',
    joinedOn: today(),
    waiverSignedAt: null,
    notes: '',
    ...fields,
  };
  state.members = [...state.members, member];
  notify();
  persistRow('insert', 'members', toRow(memberMap, member), undefined, `${member.name}`);
  return member;
}

export function updateMember(id, patch) {
  state.members = state.members.map((m) => (m.id === id ? { ...m, ...patch } : m));
  notify();
  persistRow('patch', 'members', id, toRow(memberMap, patch), 'That change');
}

export function assignPlan(memberId, planId) {
  const plan = planById(planId);
  if (!plan) return null;
  const t = today();
  const [y, m] = t.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const isCredit = [PLAN_KINDS.PACK, PLAN_KINDS.DROP_IN].includes(plan.kind);

  const membership = {
    id: nextId(),
    orgId: state.org.id,
    memberId,
    planId,
    status: 'active',
    startDate: t,
    currentPeriodStart: `${t.slice(0, 7)}-01`,
    currentPeriodEnd: `${t.slice(0, 7)}-${String(last).padStart(2, '0')}`,
    interval: plan.interval || 'month',
    amountCents: plan.amountCents,
    freezes: [],
    usageDates: [],
    creditsRemaining: plan.credits || 0,
    // The plan advertises an expiry on two screens; without stamping it at sale
    // time the entitlement engine has nothing to enforce and a 30-day drop-in
    // credit lives forever.
    creditsExpireOn: isCredit && plan.expiryDays ? addDays(t, plan.expiryDays) : null,
    pastDueCents: 0,
  };
  state.memberships = [...state.memberships, membership];
  notify();
  // freezes and usageDates are assembled from other tables at load time and
  // are not columns on this row.
  const { freezes, usageDates, ...row } = membership;
  persistRow('insert', 'memberships', toRow(membershipMap, row), undefined, `${plan.name}`);
  return membership;
}

/**
 * Put a membership on hold.
 *
 * `to` may be null for an open-ended hold — a member on deployment or medical
 * leave with no return date is a real case, and entitlements already treat a
 * missing end as "still frozen".
 */
export function freezeMembership(membershipId, { from, to, days, reason } = {}) {
  const start = from || today();
  const end = to !== undefined ? to : addDays(start, days ?? 30);
  const freezeId = nextId();
  state.memberships = state.memberships.map((m) => (m.id === membershipId
    ? { ...m, freezes: [...(m.freezes || []), { id: freezeId, from: start, to: end, reason: reason || null }] }
    : m));
  notify();
  persistRow('insert', 'membership_freezes', {
    ...toRow(freezeMap, { id: freezeId, from: start, to: end, reason: reason || null }),
    org_id: state.org.id,
    membership_id: membershipId,
  }, undefined, 'The hold');
  return { from: start, to: end };
}

/**
 * Remove a hold.
 *
 * Identified by the freeze itself rather than "whatever covers today", which
 * is what this used to do: a hold scheduled to start next month could not be
 * removed at all, because nothing about it covered today. Passing no id keeps
 * the old meaning — end the hold in effect right now.
 */
export function unfreezeMembership(membershipId, freezeId) {
  const t = today();
  const coversToday = (f) => t >= f.from && (!f.to || t <= f.to);
  const dropped = (state.memberships.find((m) => m.id === membershipId)?.freezes || [])
    .filter((f) => (freezeId ? f.id === freezeId : coversToday(f)));
  state.memberships = state.memberships.map((m) => (m.id === membershipId
    ? {
      ...m,
      freezes: (m.freezes || []).filter((f) => (freezeId ? f.id !== freezeId : !coversToday(f))),
    }
    : m));
  notify();
  for (const f of dropped) persistRow('remove', 'membership_freezes', f.id, undefined, 'The hold');
}

/**
 * End a membership.
 *
 * `atPeriodEnd` is the default a gym actually wants: the member has paid for
 * the current period, so they keep training until it runs out and simply do
 * not renew. Cancelling immediately takes away time already paid for, which is
 * a refund conversation, so it stays available but has to be asked for.
 *
 * A scheduled cancellation sets endDate and leaves the status active —
 * checkEntitlement already denies past endDate, so no new rule is needed.
 */
export function cancelMembership(membershipId, { atPeriodEnd = false } = {}) {
  const t = today();
  state.memberships = state.memberships.map((m) => {
    if (m.id !== membershipId) return m;
    if (!atPeriodEnd) return { ...m, status: 'cancelled', cancelledOn: t };
    // Never schedule an end before today; a period that has already run out
    // means the honest answer is to cancel now.
    const end = m.currentPeriodEnd && m.currentPeriodEnd > t ? m.currentPeriodEnd : t;
    return { ...m, endDate: end, cancelScheduled: true };
  });
  notify();
  const after = state.memberships.find((m) => m.id === membershipId);
  persistRow('patch', 'memberships', membershipId, atPeriodEnd
    ? { end_date: after.endDate }
    : { status: 'cancelled', cancelled_on: t }, 'The cancellation');
}

/** Undo a scheduled cancellation while it has not taken effect yet. */
export function resumeMembership(membershipId) {
  state.memberships = state.memberships.map((m) => (m.id === membershipId
    ? { ...m, endDate: null, cancelScheduled: false }
    : m));
  notify();
  persistRow('patch', 'memberships', membershipId, { end_date: null }, 'Keeping the membership');
}

/**
 * Validate a plan before it is saved.
 *
 * These are the same three constraints the database declares on the plans
 * table (capped_needs_limit, pack_needs_credits, recurring_needs_interval).
 * Checking here as well is not belt-and-braces: a Postgres CHECK violation
 * surfaces as "new row violates check constraint capped_needs_limit", which is
 * not something to show a gym owner.
 */
export function validatePlan(plan) {
  const errors = [];
  if (!plan.name?.trim()) errors.push('The plan needs a name.');
  if (!Object.values(PLAN_KINDS).includes(plan.kind)) errors.push('Choose a plan type.');
  if (plan.amountCents < 0) errors.push('Price cannot be negative.');

  if (plan.kind === PLAN_KINDS.RECURRING_CAPPED) {
    if (!plan.limit || plan.limit < 1) errors.push('A capped plan needs a limit of at least one class.');
    if (!plan.limitPeriod) errors.push('A capped plan needs a period to count against.');
  }
  if ([PLAN_KINDS.PACK, PLAN_KINDS.DROP_IN].includes(plan.kind)) {
    if (!plan.credits || plan.credits < 1) errors.push('A credit plan needs at least one credit.');
  }
  if ([PLAN_KINDS.RECURRING_UNLIMITED, PLAN_KINDS.RECURRING_CAPPED].includes(plan.kind)) {
    if (!plan.interval) errors.push('A recurring plan needs a billing interval.');
  }
  return errors;
}

export function savePlan(fields) {
  const plan = {
    kind: PLAN_KINDS.RECURRING_UNLIMITED,
    amountCents: 0,
    programs: [],
    blockWhenPastDue: false,
    active: true,
    ...fields,
  };
  const errors = validatePlan(plan);
  if (errors.length) return { ok: false, errors };

  if (plan.id && state.plans.some((p) => p.id === plan.id)) {
    state.plans = state.plans.map((p) => (p.id === plan.id ? { ...p, ...plan } : p));
    notify();
    persistRow('patch', 'plans', plan.id, toRow(planMap, planById(plan.id)), plan.name);
    return { ok: true, plan: planById(plan.id), created: false };
  }

  const created = { ...plan, id: plan.id || nextId(), orgId: state.org.id };
  state.plans = [...state.plans, created];
  notify();
  persistRow('insert', 'plans', toRow(planMap, created), undefined, created.name);
  return { ok: true, plan: created, created: true };
}

/**
 * Retire a plan without deleting it.
 *
 * Deleting would orphan every membership sold on it and every invoice that
 * references it. Deactivating stops it being sold while the people already on
 * it carry on unaffected, which is what "we don't offer that any more"
 * actually means.
 */
export function setPlanActive(planId, active) {
  state.plans = state.plans.map((p) => (p.id === planId ? { ...p, active } : p));
  notify();
  persistRow('patch', 'plans', planId, { active }, 'The plan');
  const holders = state.memberships.filter(
    (m) => m.planId === planId && m.status === 'active',
  ).length;
  return { ok: true, holders };
}

export function saveBookingPolicy(patch) {
  const next = { ...state.policy, ...patch };
  const errors = [];
  if (next.opensHoursBefore < 0) errors.push('Booking cannot open a negative time before the class.');
  if (next.closesMinutesBefore < 0) errors.push('Booking cannot close after the class starts.');
  if (next.waitlist && next.waitlistCap < 1) errors.push('A waitlist needs room for at least one person.');
  for (const [key, label] of [['lateCancelFeeCents', 'Late cancel fee'], ['noShowFeeCents', 'No-show fee']]) {
    if (next[key] < 0) errors.push(`${label} cannot be negative.`);
  }
  if (errors.length) return { ok: false, errors };

  state.policy = next;
  notify();
  persistRow('patch', 'booking_policies', state.org.id, toRow(policyMap, next), 'The booking policy');
  return { ok: true, policy: next };
}

export function recordPayment(invoiceId) {
  state.invoices = state.invoices.map((i) => (i.id === invoiceId
    ? { ...i, status: 'paid', paidOn: today() }
    : i));
  const invoice = state.invoices.find((i) => i.id === invoiceId);
  if (invoice) {
    state.memberships = state.memberships.map((m) => (m.memberId === invoice.memberId
      ? { ...m, pastDueCents: 0 }
      : m));
  }
  notify();
  persistRow('patch', 'invoices', invoiceId, { status: 'paid', paid_on: today() }, 'The payment');
}

export function retryPayment(invoiceId) {
  state.invoices = state.invoices.map((i) => (i.id === invoiceId
    ? { ...i, attemptsMade: (i.attemptsMade || 0) + 1, lastAttemptOn: today() }
    : i));
  notify();
  const after = state.invoices.find((i) => i.id === invoiceId);
  persistRow('patch', 'invoices', invoiceId,
    { attempts_made: after.attemptsMade, last_attempt_on: after.lastAttemptOn }, 'The retry');
}
